import React, { useEffect, useState } from 'react';
import { array, bool, object, string } from 'prop-types';
import classNames from 'classnames';

import { parse } from '../../util/urlHelpers';
import {
  Footer,
  LayoutSingleColumn,
  LayoutWrapperFooter,
  LayoutWrapperMain,
  LayoutWrapperTopbar,
  LinkTabNavHorizontal,
  Page,
  PaginationLinks,
} from '../../components';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import PrismicSectionHero from '../../components/PrismicSectionHero/PrismicSectionHero';
import BlogListing from '../../components/BlogListing/BlogListing';
import { formatBlogPrismicData } from '../../util/data';
import { COACHING_BLOGS, COACHING_CULTURE_BLOGS, MOST_RECENT_BLOGS } from '../../marketplace-custom-config';
import { propTypes } from '../../util/types';

import css from './BlogPage.module.css';

const formatDateOptions = { year: 'numeric', month: 'long', day: 'numeric' };

const BlogPageComponent = props => {
  const {
    fetchBlogsInProgress,
    fetchBlogsError,
    pagination,
    blogs = [],
    scrollingDisabled,
    intl,
    newestBlog,
    searchParams,
  } = props;

  const [currentTab, setCurrentTab] = useState(MOST_RECENT_BLOGS);
  const { category } = parse(searchParams);

  useEffect(() => {
    if (category) {
      setCurrentTab(category);
    }
  }, [searchParams]);

  const { slug = '', postDate, writer, title, image, alt } = formatBlogPrismicData(newestBlog);
  const schemaTitle = intl.formatMessage({ id: 'BlogPage.schemaTitle' });
  const tabs = [
    {
      text: (
        <span className={classNames({ [css.activeTab]: currentTab === MOST_RECENT_BLOGS })}>
          <FormattedMessage id="BlogPage.mostRecent" />
        </span>
      ),
      selected: currentTab === MOST_RECENT_BLOGS,
      linkProps: {
        name: 'BlogPage',
        to: { search: '?category=most_recent' }
      },
    },
    {
      text: (
        <span className={classNames({ [css.activeTab]: currentTab === COACHING_BLOGS })}>
          <FormattedMessage id="BlogPage.coaching" />
        </span>
      ),
      selected: currentTab === COACHING_BLOGS,
      linkProps: {
        name: 'BlogPage',
        to: { search: '?category=coaching' }
      },
    },
    {
      text: (
        <span className={classNames({ [css.activeTab]: currentTab === COACHING_CULTURE_BLOGS })}>
          <FormattedMessage id="BlogPage.coachingCulture" />
        </span>
      ),
      linkProps: {
        name: 'BlogPage',
        to: { search: '?category=coaching_culture' }
      },
      selected: currentTab === COACHING_CULTURE_BLOGS,
    },
  ];

  const dateAndWriter = intl.formatMessage(
    { id: 'BlogPage.dateAndWriter' },
    {
      date: postDate ? postDate.toLocaleDateString('en-US', formatDateOptions) : null,
      writer,
    }
  );
  const featuredPostText = intl.formatMessage({ id: 'BlogPage.featuredPost' });

  const paginationLinks =
    pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName="BlogPage"
        pagination={pagination}
        pageSearchParams={parse(searchParams)}
      />
    ) : null;

  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      title={schemaTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        name: schemaTitle,
      }}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer hideSearchbar currentPage="BlogPage" />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain>
          <PrismicSectionHero linkParams={{ slug }} backgroundImage={image} alt={alt}>
            <div>
              <div className={css.title}>{title}</div>
              <div className={css.line} />
              <div className={css.dateAndWriter}>{dateAndWriter}</div>
              <div className={css.featuredPost}>{featuredPostText}</div>
            </div>
          </PrismicSectionHero>
          <LinkTabNavHorizontal tabs={tabs} className={css.tabs} />
          {fetchBlogsError && (
            <p className={css.error}>
              <FormattedMessage id="BlogPage.errorBlogs" />
            </p>
          )}
          {fetchBlogsInProgress ? (
            <p className={css.loading}>
              <FormattedMessage id="BlogPage.loadingBlogs" />
            </p>
          ) : (
            <>
              <div className={css.blogs}>
                {blogs.map(blog => (
                  <BlogListing key={blog.id} blog={blog} className={css.blog} />
                ))}
              </div>
              {paginationLinks}
            </>
          )}
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </Page>
  );
};

BlogPageComponent.defaultProps = {
  fetchBlogsInProgress: false,
  fetchBlogsError: null,
  pagination: null,
  blogs: [],
  scrollingDisabled: false,
  newestBlog: null,
  searchParams: null,
};

BlogPageComponent.propTypes = {
  fetchBlogsInProgress: bool,
  fetchBlogsError: propTypes.error,
  pagination: propTypes.pagination,
  blogs: array,
  scrollingDisabled: bool.isRequired,
  newestBlog: object,
  searchParams: string,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const {
    fetchBlogsInProgress,
    fetchBlogsError,
    pagination,
    blogs,
    newestBlog,
    searchParams,
  } = state.BlogPage;

  return {
    searchParams,
    fetchBlogsInProgress,
    fetchBlogsError,
    pagination,
    blogs,
    newestBlog,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const BlogPage = compose(
  connect(mapStateToProps),
  injectIntl
)(BlogPageComponent);

export default BlogPage;
